import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator, initializeAuth, inMemoryPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { handoffThisLoad } from "./handoff";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getMessaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MSGID,
  appId: import.meta.env.VITE_FIREBASE_APPID
};

const app = initializeApp(firebaseConfig);

// A handoff load (native's web view keeps nothing, decision 83) keeps its
// session in memory and never touches storage (handoff.ts, 2 Nov).
export const auth = handoffThisLoad()
  ? initializeAuth(app, { persistence: inMemoryPersistence, popupRedirectResolver: browserPopupRedirectResolver })
  : getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);
// Instagram iOS WebView blocks PushManager — getMessaging() throws at module load
// and crashes the app before React renders. Only initialize when supported.
export const messaging = ('serviceWorker' in navigator && 'PushManager' in window)
  ? getMessaging(app)
  : null;

// Connect to emulators if USE_EMULATORS is true
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
// VITE_USE_AUTH_EMULATOR=false opts out of the Auth emulator while keeping
// Firestore/Storage/Functions emulators active. Needed when running against
// the local CMS, which uses real Firebase Admin to verify tokens.
const useAuthEmulator = useEmulators && import.meta.env.VITE_USE_AUTH_EMULATOR !== 'false';

// Emulator ports default to firebase.json's. VITE_EMULATOR_*_PORT moves them,
// so a second emulator suite (firebase.overnight.json) can run beside the first.
const emulatorPort = (name: string, fallback: number) =>
  Number(import.meta.env[`VITE_EMULATOR_${name}_PORT`] ?? fallback);
const AUTH_PORT = emulatorPort('AUTH', 9099);
const FIRESTORE_PORT = emulatorPort('FIRESTORE', 8080);
const STORAGE_PORT = emulatorPort('STORAGE', 9199);
const FUNCTIONS_PORT = emulatorPort('FUNCTIONS', 5001);

if (useEmulators) {
  console.log('🔧 Using Firebase Emulators');

  try {
    if (useAuthEmulator) {
      connectAuthEmulator(auth, `http://127.0.0.1:${AUTH_PORT}`, { disableWarnings: true });
    }

    // Connect to Firestore Emulator
    connectFirestoreEmulator(db, '127.0.0.1', FIRESTORE_PORT);

    // Connect to Storage Emulator
    connectStorageEmulator(storage, '127.0.0.1', STORAGE_PORT);

    // Connect to Functions Emulator
    connectFunctionsEmulator(functions, '127.0.0.1', FUNCTIONS_PORT);

    console.log('✓ Connected to emulators:');
    if (useAuthEmulator) console.log(`  - Auth: http://127.0.0.1:${AUTH_PORT}`);
    else console.log('  - Auth: real Firebase (CMS session integration mode)');
    console.log(`  - Firestore: http://127.0.0.1:${FIRESTORE_PORT}`);
    console.log(`  - Storage: http://127.0.0.1:${STORAGE_PORT}`);
    console.log(`  - Functions: http://127.0.0.1:${FUNCTIONS_PORT}`);
    console.log('  - Emulator UI: http://localhost:4000');
  } catch (error) {
    console.warn('Error connecting to emulators (this is usually fine):', error);
  }
}
